import React, { useState, useMemo, useRef } from "react";
import { Grid, Typography, Box, Skeleton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
} from "recharts";
import DashboardCard from "../DashboardCard";
import {
  lastYearDate,
  todayDate,
} from "app/shared/ReuseComponents/DateFormatter";
import { getLastWeekRange } from "app/shared/ReuseComponents/StyledComponents";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useLateRegularizationActionAPIMutation,
  usePendingApprovalsCountAPIQuery,
  useTodayDashboardCountAPIQuery,
  useEmployeecheckinCRUDAPIMutation,
  useTotalEmployeesAPIQuery,
  useDepartmentWiseAttritionRateAPIQuery,
  useLeaveUsageTrendViewAPIQuery,
  useLateCheckinListAPIViewQuery,
  usePendingLeaveRequestsAPIQuery,
  useEmployeeCheckinSummaryAPIQuery,
} from "app/services/RTK_Apis/dashbords/ManagerDashboard";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import JumboContentLayoutMain from "@jumbo/components/JumboContentLayout/JumboContentLayoutMain";
import { Tenantname } from "app/services/auth-services";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useLateCheckinListAPIQuery } from "app/services/RTK_Apis/TimeMangament/LateCheckInApi";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const transformAttendanceData = (rawData) => {
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Initialize groupedByDay with all days having Present and Absent zero
  const groupedByDay = daysOfWeek.reduce((acc, day) => {
    acc[day] = { day, Present: 0, Absent: 0 };
    return acc;
  }, {});

  // Populate actual data from rawData
  rawData?.forEach((record) => {
    const date = new Date(record.Date); // Ensure your date field matches here
    const day = daysOfWeek[date.getDay()];

    if (record.Present_Flag === true) {
      groupedByDay[day].Present += 1;
    } else {
      groupedByDay[day].Absent += 1;
    }
  });

  return Object.values(groupedByDay);
};

const processLeaveData = (response) => {
  const statusCount = {};

  // Helper function to process leave arrays
  const processLeaves = (leaves) => {
    leaves.forEach((leave) => {
      const status = leave.Leave_flg;
      statusCount[status] = (statusCount[status] || 0) + 1;
    });
  };

  // Process both monthly and quarterly data
  response?.monthly_trend?.forEach((month) =>
    processLeaves(month.monthly_data_list),
  );

  response?.quarterly_trend?.forEach((quarter) =>
    processLeaves(quarter.quarterly_data_list),
  );

  // Convert to array format for PieChart
  return Object.entries(statusCount).map(([name, value]) => ({
    name,
    value,
  }));
};

const ManagerDashboard = () => {
  const companyId = parseInt(localStorage.getItem("OrganizationId"));
  const today = new Date().toISOString().slice(0, 10);

  const navigate = useNavigate();
  const dateRange = useMemo(
    () => ({ start_date: lastYearDate, end_date: todayDate }),
    [],
  );

  const { start_date, end_date } = getLastWeekRange();
  const [openLateDialog, setOpenLateDialog] = useState(false);
  const [latePage, setLatePage] = useState(1);

  // const lateCheckinCount = lateCheckinApiData?.count ?? 0;
  const [approvalStatus, setApprovalStatus] = useState({});

  const calculateLateHours = (checkinTime) => {
    if (!checkinTime) return "--";

    // Office reporting time = 09:30 AM
    const officeHour = 9;
    const officeMinute = 30;

    const [h, m, s] = checkinTime.split(":").map(Number);

    // Convert to minutes
    const checkinTotalMinutes = h * 60 + m;
    const officeTotalMinutes = officeHour * 60 + officeMinute;

    const lateMinutes = checkinTotalMinutes - officeTotalMinutes;

    if (lateMinutes <= 0) return "On Time";

    const hours = Math.floor(lateMinutes / 60);
    const minutes = lateMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const {
    data: todayKpi,
    isLoading: kpiLoading,
    error: kpiError,
  } = useTodayDashboardCountAPIQuery({
    start_date: today,
    end_date: today,
  });

  const { data: pendingApprovalKpi, isLoading: pendingApprovalLoading } =
    usePendingApprovalsCountAPIQuery({
      start_date: today,
      end_date: today,
      company_id: companyId,
    });

  const totalEmployeesArgs = useMemo(
    () => ({ ...dateRange, company_id: companyId }),
    [dateRange, companyId],
  );

  const { data: totalEmployeesData } = useTotalEmployeesAPIQuery(
    totalEmployeesArgs,
    {
      skip: !companyId,
    },
  );

  const { data: departmentData, isLoading: departmentLoading } =
    useDepartmentWiseAttritionRateAPIQuery(
      {
        ...dateRange,
        company_id: companyId,
      },
      { skip: !companyId },
    );

  const leaveUsageArgs = useMemo(
    () => ({
      start_date: lastYearDate,
      end_date: todayDate,
      company_id: companyId,
    }),
    [companyId],
  );

  const { data: leaveUsageData, isLoading: leaveUsageLoading } =
    useLeaveUsageTrendViewAPIQuery(leaveUsageArgs, { skip: !companyId });

  const lateCheckinArgs = useMemo(
    () => ({
      // Company_Id: companyId,
      // start_date: "2026-02-05",
      // end_date: "2026-02-05",
      start_date: todayDate,
      end_date: todayDate,
      page: latePage,
    }),
    [companyId, latePage],
  );

  const { data: lateCheckinApiData, isFetching: lateCheckinLoading } =
    useLateCheckinListAPIQuery(lateCheckinArgs, {
      skip: !companyId,
      refetchOnMountOrArgChange: true,
    });

  const employeeCheckinArgs = useMemo(
    () => ({ start_date, end_date, company_id: companyId }),
    [start_date, end_date, companyId],
  );

  const { data: employeeCheckinData, isLoading: employeeCheckinLoading } =
    useEmployeeCheckinSummaryAPIQuery(employeeCheckinArgs, {
      skip: !companyId,
    });

  const pendingLeaveArgs = useMemo(
    () => ({
      start_date: lastYearDate,
      end_date: todayDate,
      company_id: companyId,
    }),
    [companyId],
  );

  const { data: pendingLeaveData, isLoading: pendingLeaveLoading } =
    usePendingLeaveRequestsAPIQuery(pendingLeaveArgs, { skip: !companyId });

  // const totalActiveEmployees = totalEmployeesSuccess
  //   ? totalEmployeesData.results.active_employees_count
  //   : null;

  const chartsLoading = useMemo(
    () =>
      departmentLoading ||
      leaveUsageLoading ||
      lateCheckinLoading ||
      employeeCheckinLoading ||
      pendingLeaveLoading,
    [
      departmentLoading,
      leaveUsageLoading,
      lateCheckinLoading,
      employeeCheckinLoading,
      pendingLeaveLoading,
    ],
  );

  const lateCheckins = lateCheckinApiData?.results ?? [];
  const hasNext = Boolean(lateCheckinApiData?.next);
  const hasPrev = Boolean(lateCheckinApiData?.previous);
  const isLoading = chartsLoading || kpiLoading;
  const [employeecheckinCRUD, { isLoading: actionLoading }] =
    useEmployeecheckinCRUDAPIMutation();

  const handleApproveRegularization = async (item) => {
    try {
      await employeecheckinCRUD({
        id: item.id,
      }).unwrap();

      setApprovalStatus((prev) => ({
        ...prev,
        [item.id]: "approved",
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleRejectRegularization = async (item) => {
    setApprovalStatus((prev) => ({
      ...prev,
      [item.id]: "rejected",
    }));
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Pending leave, leave usage and late checkins are provided by RTK Query hooks.

  const leaveStatusData = useMemo(
    () => processLeaveData(leaveUsageData),
    [leaveUsageData],
  );

  // useEffect(() => {
  //   if (lateCheckinApiData) {
  //     setLateCheckins(lateCheckinApiData?.results || []);
  //     setData((prev) => ({
  //       ...(prev || {}),
  //       lateCheckinData: lateCheckinApiData?.count,
  //     }));
  //   }
  // }, [lateCheckinApiData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const dialogRef = React.useRef(null);

  const lateCheckinFromDate = todayDate;

  const lateCheckinDisplayDate = formatDate(lateCheckinFromDate);

  const formatTime = (timeStr) => {
    if (!timeStr) return "--";
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(h, m);
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const chartData = useMemo(
    () =>
      transformAttendanceData(
        employeeCheckinData?.employee_checkins_list ?? [],
      ),
    [employeeCheckinData],
  );

  const chartDepartments = useMemo(
    () => departmentData?.results ?? [],
    [departmentData],
  );

  const navigateEmployees = () => {
    navigate("/hr/list-employee");
  };

  const navigateCheckinStatus = () => {
    navigate("/hr/HrOperation/CheckinStatus");
  };

  const navigatePendingApprovals = () => {
    navigate("/samples/content-layout");
  };

  const navigateCheckinList = () => {
    navigate("/hr/CheckinlistsESS");
  };

  const navigateLeaveList = () => {
    navigate("/hr/list-of-leaves");
  };

  const navigatePendingExpenses = () => {
    navigate("/hr/EmployeeClaimsList");
  };

  const approvedLeaveCount = todayKpi?.approved_leaves_count ?? 0;
  const pendingLeaveCount = useMemo(
    () =>
      todayKpi?.pending_leaves_count ??
      pendingLeaveData?.pending_leave_request_count ??
      0,
    [todayKpi, pendingLeaveData],
  );
  const pendingExpenseCount = todayKpi?.pending_expenses_count ?? 0;
  const lateCheckinCount = todayKpi?.late_checkin_count ?? 0;
  const pendingApprovalCount = pendingApprovalKpi?.pending_approvals_count ?? 0;
  const handleLateCheckinClick = () => {
    setLatePage(1); // reset
    setOpenLateDialog(true);
  };

  // show skeleton while any chart data is loading

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "calc(100vh - 64px)", // minus top navbar
        // backgroundColor: "background.default",
      }}
    >
      {Tenantname === "relux" ? (
        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
            // p: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              // px: { xs: 2, sm: 2, md: 2 },
              overflowX: "hidden",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1rem", sm: "1rem", md: "1.2rem" },
                fontWeight: 500,
                mb: 2,
              }}
            >
              Manager Dashboard
            </Typography>

            <Grid
              container
              spacing={{ xs: 1.5, sm: 2, md: 3 }}
              alignItems="stretch"
            >
              {/* Row 1 */}
              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Total Active Employees"
                    value={
                      totalEmployeesData?.results?.active_employees_count ?? 0
                    }
                    icon={<GroupsRoundedIcon />}
                    color="#6366F1"
                    onClick={navigateEmployees}
                    // sx={{
                    //   minHeight: { xs: 100, sm: 120, md: 150 },
                    //   height: "100%",
                    //   display: "flex",
                    //   flexDirection: "column",
                    //   justifyContent: "center",

                    //   borderRadius: "16px",
                    //   background: "#ffffff",
                    //   borderLeft: "6px solid #4F46E5",
                    //   boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    //   transition: "all 0.25s ease",
                    //   "&:hover": {
                    //     transform: "translateY(-6px)",
                    //     boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                    //   },
                    // }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Total Presents"
                    value={
                      (
                        employeeCheckinData?.employee_checkins_list ?? []
                      ).filter((opt) => opt?.Date === todayDate)?.length || 0
                    }
                    icon={<LoginRoundedIcon />}
                    color="#22C55E"
                    onClick={navigateCheckinStatus}
                    // sx={{
                    //   minHeight: { xs: 100, sm: 120, md: 150 },
                    //   height: "100%",
                    //   display: "flex",
                    //   flexDirection: "column",
                    //   justifyContent: "center",

                    //   borderRadius: "16px",
                    //   background: "#ffffff",
                    //   borderLeft: "6px solid #059669",
                    //   boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    //   transition: "all 0.25s ease",
                    //   "&:hover": {
                    //     transform: "translateY(-6px)",
                    //     boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                    //   },
                    // }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Late Check-ins"
                    value={lateCheckinCount}
                    icon={<ScheduleRoundedIcon />}
                    color="#F59E0B"
                    onClick={handleLateCheckinClick}
                  />
                )}
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Leave (Approved/Pending)"
                    value={`${approvedLeaveCount} / ${pendingLeaveCount}`}
                    onClick={navigateLeaveList}
                    icon={<EventBusyRoundedIcon />}
                    color="#EF4444"
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Pending Approvals"
                    value={pendingApprovalCount}
                    onClick={navigatePendingApprovals}
                    icon={<AssignmentTurnedInRoundedIcon />}
                    color="#0EA5E9"
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Pending Reimbursements"
                    value={pendingExpenseCount}
                    onClick={navigatePendingExpenses}
                    icon={<ReceiptLongRoundedIcon />}
                    color="#A855F7"
                  />
                )}
              </Grid>
            </Grid>

            <Box mt={4}>
              {chartsLoading ? (
                <LoadingSkeleton />
              ) : (
                <Grid container spacing={3}>
                  {/* Attendance Stats */}
                  <Grid item xs={12} md={6}>
                    <ChartBox
                      title="Team Attendance (Past Week)"
                      onShowMore={navigateCheckinList}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 220 : 300}
                      >
                        <BarChart data={chartData}>
                          <XAxis
                            dataKey="day"
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                          />
                          <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />

                          <RechartsTooltip />

                          <Legend />
                          <Bar dataKey="Present" fill="#00C49F" />
                          <Bar dataKey="Absent" fill="#FF5E57" />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Leave Requests Pie */}
                  <Grid item xs={12} md={6}>
                    <ChartBox
                      title="Leave Requests Status"
                      onShowMore={navigateLeaveList}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 220 : 300}
                      >
                        <PieChart>
                          <Pie
                            data={leaveStatusData}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={100}
                            label
                          >
                            {leaveStatusData?.map((entry, index) => (
                              <Cell
                                key={`cell-leave-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />

                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Performance Distribution Pie */}
                  <Grid item xs={12}>
                    {/* <ChartBox title="Team Performance Distribution"> */}
                    <ChartBox title="Department-wise Employees">
                      {/* <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <PieChart>
                  <Pie
                    data={data.performance_distribution}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {data.performance_distribution?.map((entry, index) => (
                      <Cell key={`cell-perf-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer> */}

                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 260 : 360}
                      >
                        {isMobile ? (
                          // 📱 MOBILE — COLUMN CHART
                          <BarChart
                            data={chartDepartments}
                            margin={{
                              top: 20,
                              right: 10,
                              left: 10,
                              bottom: 40,
                            }}
                          >
                            <XAxis
                              dataKey="department"
                              angle={-35}
                              textAnchor="end"
                              interval={0}
                              tick={{ fontSize: 10 }}
                            />

                            <YAxis
                              allowDecimals={false}
                              tick={{ fontSize: 11 }}
                            />

                            <RechartsTooltip />

                            <Bar
                              dataKey="total_employees_count"
                              fill="#1976d2"
                              radius={[8, 8, 0, 0]}
                              maxBarSize={28}
                            />
                          </BarChart>
                        ) : (
                          // 🖥 DESKTOP — HORIZONTAL BAR
                          <BarChart
                            data={chartDepartments}
                            layout="vertical"
                            margin={{
                              top: 10,
                              right: 30,
                              left: 120,
                              bottom: 10,
                            }}
                          >
                            <XAxis type="number" allowDecimals={false} />

                            <YAxis
                              type="category"
                              dataKey="department"
                              width={140}
                              tick={{ fontSize: 12 }}
                            />

                            <RechartsTooltip />

                            <Bar
                              dataKey="total_employees_count"
                              fill="#1976d2"
                              barSize={22}
                              radius={[0, 10, 10, 0]}
                            />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Skill Gap Categories Pie */}
                  {/* <Grid item xs={12} md={6}>
            <ChartBox title="">
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <PieChart>
                  <Pie
                    data={data.skill_gap_categories}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {data.skill_gap_categories?.map((entry, index) => (
                      <Cell
                        key={`cell-skill-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartBox>
          </Grid> */}
                </Grid>
              )}
            </Box>
            <Dialog
              open={openLateDialog}
              onClose={() => setOpenLateDialog(false)}
              fullScreen={isMobile}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  width: "760px",
                  maxWidth: "90vw",
                  borderRadius: 3,
                },
              }}
            >
              {/* Dialog Header */}
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Late Check-ins
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    • {lateCheckinDisplayDate}
                  </Typography>
                </Typography>

                <IconButton onClick={() => setOpenLateDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <Divider />

              {/* Dialog Content */}
              <DialogContent
                dividers
                ref={dialogRef}
                sx={{
                  p: 0,
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                {/* Header Row */}
                <Box
                  sx={{
                    display: { xs: "block", sm: "grid" },
                    gridTemplateColumns: { sm: "2.2fr 1fr 1fr 1.2fr" },

                    px: 2.5,
                    py: 1,
                    backgroundColor: "grey.100",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    Employee
                  </Typography>
                  {/* <Typography variant="caption" fontWeight={600}>Date</Typography> */}
                  <Typography variant="caption" fontWeight={600}>
                    Check In
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    Late By
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    Action
                  </Typography>
                </Box>

                {/* Rows */}
                {lateCheckinLoading ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="text" width={160} />
                    <Skeleton variant="text" width={220} />
                  </Box>
                ) : lateCheckins.length > 0 ? (
                  lateCheckins.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: { xs: "block", sm: "grid" },
                        gridTemplateColumns: { sm: "2.2fr 1fr 1fr 1.2fr" },

                        alignItems: "center",
                        px: 2.5,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {/* Employee */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: "primary.main",
                            fontSize: 13,
                          }}
                        >
                          {item.Employee_name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {item.Employee_name}
                        </Typography>
                      </Box>

                      {/* Date
      <Typography variant="body2" color="text.secondary">
        {formatDate(item.Checkin_Date)}
      </Typography> */}

                      {/* Check-in */}
                      <Typography variant="body2" color="text.secondary">
                        {formatTime(item.Checkin_Time)}
                      </Typography>

                      {/* Late Hours */}
                      <Typography variant="caption" fontWeight={600}>
                        {calculateLateHours(item.Checkin_Time)}
                      </Typography>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                        {approvalStatus[item.id] === "approved" ? (
                          <Tooltip title="Approved" arrow>
                            <CheckCircleRoundedIcon
                              sx={{
                                color: "#52db85",
                                fontSize: 24,
                              }}
                            />
                          </Tooltip>
                        ) : approvalStatus[item.id] === "rejected" ? (
                          <Tooltip title="Rejected" arrow>
                            <CancelRoundedIcon
                              sx={{
                                color: "#d86b6b",
                                fontSize: 24,
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Approve" arrow>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleApproveRegularization(item)
                                }
                                sx={{
                                  color: "#52db85",
                                  "&:hover": { backgroundColor: "#ecfdf5" },
                                }}
                              >
                                <CheckCircleRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Reject" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleRejectRegularization(item)}
                                sx={{
                                  color: "#d86b6b",
                                  "&:hover": { backgroundColor: "#fef2f2" },
                                }}
                              >
                                <CancelRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No late check-ins available
                    </Typography>
                  </Box>
                )}
                {/* Pagination arrows */}
                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #eee",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    disabled={!hasPrev || lateCheckinLoading}
                    onClick={() => {
                      setLatePage((p) => p - 1);
                      dialogRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }}
                  >
                    <ChevronLeftRoundedIcon />
                  </IconButton>

                  <IconButton
                    size="small"
                    disabled={!hasNext || lateCheckinLoading}
                    onClick={() => {
                      setLatePage((p) => p + 1);
                      dialogRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }}
                  >
                    <ChevronRightRoundedIcon />
                  </IconButton>
                </Box>
              </DialogContent>
            </Dialog>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
            // p: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              // px: { xs: 2, sm: 2, md: 2 },
              overflowX: "hidden",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1rem", sm: "1rem", md: "1.2rem" },
                fontWeight: 500,
                mb: 2,
              }}
            >
              Manager Dashboard
            </Typography>

            <Grid
              container
              spacing={{ xs: 1.5, sm: 2, md: 3 }}
              alignItems="stretch"
            >
              {/* Row 1 */}
              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Total Active Employees"
                    value={
                      totalEmployeesData?.results?.active_employees_count ?? 0
                    }
                    icon={<GroupsRoundedIcon />}
                    color="#6366F1"
                    onClick={navigateEmployees}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Total Presents"
                    value={
                      (
                        employeeCheckinData?.employee_checkins_list ?? []
                      ).filter((opt) => opt?.Date === todayDate)?.length || 0
                    }
                    icon={<LoginRoundedIcon />}
                    color="#22C55E"
                    onClick={navigateCheckinStatus}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Late Check-ins"
                    value={lateCheckinCount}
                    icon={<ScheduleRoundedIcon />}
                    color="#F59E0B"
                    onClick={handleLateCheckinClick}
                  />
                )}
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Leave (Approved/Pending)"
                    value={`${approvedLeaveCount} / ${pendingLeaveCount}`}
                    icon={<EventBusyRoundedIcon />}
                    color="#EF4444"
                    onClick={navigateLeaveList}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="20%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Pending Approvals"
                    value={pendingApprovalCount}
                    icon={<AssignmentTurnedInRoundedIcon />}
                    color="#0EA5E9"
                    onClick={navigatePendingApprovals}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                {isLoading ? (
                  <Box
                    sx={{
                      minHeight: { xs: 100, sm: 120, md: 150 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",

                      borderRadius: "16px",
                      background: "#fff",
                      p: 2,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="40%" height={36} />
                  </Box>
                ) : (
                  <DashboardCard
                    title="Pending Reimbursements"
                    value={pendingExpenseCount}
                    icon={<ReceiptLongRoundedIcon />}
                    color="#A855F7"
                    onClick={navigatePendingExpenses}
                  />
                )}
              </Grid>
            </Grid>

            <Box mt={4}>
              {chartsLoading ? (
                <LoadingSkeleton />
              ) : (
                <Grid container spacing={3}>
                  {/* Attendance Stats */}
                  <Grid item xs={12} md={6}>
                    <ChartBox
                      title="Team Attendance (Past Week)"
                      onShowMore={navigateCheckinList}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 220 : 300}
                      >
                        <BarChart data={chartData}>
                          <XAxis
                            dataKey="day"
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                          />
                          <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />

                          <RechartsTooltip />

                          <Legend />
                          <Bar dataKey="Present" fill="#00C49F" />
                          <Bar dataKey="Absent" fill="#FF5E57" />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Leave Requests Pie */}
                  <Grid item xs={12} md={6}>
                    <ChartBox
                      title="Leave Requests Status"
                      onShowMore={navigateLeaveList}
                    >
                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 220 : 300}
                      >
                        <PieChart>
                          <Pie
                            data={leaveStatusData}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={100}
                            label
                          >
                            {leaveStatusData?.map((entry, index) => (
                              <Cell
                                key={`cell-leave-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <RechartsTooltip />

                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Performance Distribution Pie */}
                  <Grid item xs={12}>
                    {/* <ChartBox title="Team Performance Distribution"> */}
                    <ChartBox title="Department-wise Employees">
                      {/* <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <PieChart>
                  <Pie
                    data={data.performance_distribution}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {data.performance_distribution?.map((entry, index) => (
                      <Cell key={`cell-perf-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer> */}

                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 260 : 360}
                      >
                        {isMobile ? (
                          // 📱 MOBILE — COLUMN CHART
                          <BarChart
                            data={chartDepartments}
                            margin={{
                              top: 20,
                              right: 10,
                              left: 10,
                              bottom: 40,
                            }}
                          >
                            <XAxis
                              dataKey="department"
                              angle={-35}
                              textAnchor="end"
                              interval={0}
                              tick={{ fontSize: 10 }}
                            />

                            <YAxis
                              allowDecimals={false}
                              tick={{ fontSize: 11 }}
                            />

                            <RechartsTooltip />

                            <Bar
                              dataKey="total_employees_count"
                              fill="#1976d2"
                              radius={[8, 8, 0, 0]}
                              maxBarSize={28}
                            />
                          </BarChart>
                        ) : (
                          // 🖥 DESKTOP — HORIZONTAL BAR
                          <BarChart
                            data={chartDepartments}
                            layout="vertical"
                            margin={{
                              top: 10,
                              right: 30,
                              left: 120,
                              bottom: 10,
                            }}
                          >
                            <XAxis type="number" allowDecimals={false} />

                            <YAxis
                              type="category"
                              dataKey="department"
                              width={140}
                              tick={{ fontSize: 12 }}
                            />

                            <RechartsTooltip />

                            <Bar
                              dataKey="total_employees_count"
                              fill="#1976d2"
                              barSize={22}
                              radius={[0, 10, 10, 0]}
                            />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </ChartBox>
                  </Grid>

                  {/* Skill Gap Categories Pie */}
                  {/* <Grid item xs={12} md={6}>
            <ChartBox title="">
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <PieChart>
                  <Pie
                    data={data.skill_gap_categories}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label
                  >
                    {data.skill_gap_categories?.map((entry, index) => (
                      <Cell
                        key={`cell-skill-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartBox>
          </Grid> */}
                </Grid>
              )}
            </Box>

            {/* for late checkin lists */}
            <Dialog
              open={openLateDialog}
              onClose={() => setOpenLateDialog(false)}
              fullScreen={isMobile}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  width: "660px",
                  maxWidth: "70vw",
                  borderRadius: 3,
                },
              }}
            >
              {/* Dialog Header */}
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Late Check-ins
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    • {lateCheckinDisplayDate}
                  </Typography>
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => setOpenLateDialog(false)}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <Divider />

              {/* Dialog Content */}
              <DialogContent
                dividers
                ref={dialogRef}
                sx={{
                  p: 0,
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                {/* Header Row */}
                <Box
                  sx={{
                    display: { xs: "block", sm: "grid" },
                    gridTemplateColumns: { sm: "2.2fr 1fr 1fr" },
                    px: 2.5,
                    py: 1,
                    backgroundColor: "grey.100",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    Employee
                  </Typography>
                  {/* <Typography variant="caption" fontWeight={600}>Date</Typography> */}
                  <Typography variant="caption" fontWeight={600}>
                    Check In
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    Late By
                  </Typography>
                  {/* <Typography variant="caption" fontWeight={600}>
              Action
            </Typography> */}
                </Box>

                {/* Rows */}
                {lateCheckins?.length > 0 ? (
                  <>
                    {lateCheckins.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: { xs: "block", sm: "grid" },
                          gridTemplateColumns: { sm: "2.2fr 1fr 1fr" },
                          alignItems: "center",
                          px: 2.5,
                          py: 1,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {/* Employee */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: "primary.main",
                              fontSize: 13,
                            }}
                          >
                            {item.Employee_name?.charAt(0)}
                          </Avatar>

                          <Typography variant="body2" fontWeight={500} noWrap>
                            {item.Employee_name}
                          </Typography>
                        </Box>

                        {/* Check In */}
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(item.Checkin_Time)}
                        </Typography>

                        {/* Late */}
                        <Typography variant="caption" fontWeight={600}>
                          {calculateLateHours(item.Checkin_Time)}
                        </Typography>
                      </Box>
                    ))}

                    {/*  Pagination ONLY ONCE */}
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "#fff",
                        borderTop: "1px solid #eee",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        disabled={!hasPrev || lateCheckinLoading}
                        onClick={() => {
                          setLatePage((p) => p - 1);
                          dialogRef.current?.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        <ChevronLeftRoundedIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        disabled={!hasNext || lateCheckinLoading}
                        onClick={() => {
                          setLatePage((p) => p + 1);
                          dialogRef.current?.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                      >
                        <ChevronRightRoundedIcon />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No late check-ins available
                    </Typography>
                  </Box>
                )}
              </DialogContent>
            </Dialog>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Reusable Chart Container
const ChartBox = ({ title, children, onShowMore }) => (
  <Box
    sx={{
      p: { xs: 2, sm: 3 },
      borderRadius: 3,
      width: "100%",
      height: "100%",
      backgroundColor: "background.paper",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.09)",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>

      {onShowMore && (
        <Typography
          variant="body2"
          color="primary.main"
          sx={{
            cursor: "pointer",
            fontWeight: 500,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={onShowMore}
        >
          Show more →
        </Typography>
      )}
    </Box>

    {children}
  </Box>
);

export default ManagerDashboard;

const LoadingSkeleton = () => (
  <Box>
    <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
    <Grid container spacing={3} mb={4}>
      {[0, 1, 2].map((i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Box
            sx={{
              minHeight: { xs: 100, sm: 120, md: 150 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",

              borderRadius: "16px",
              background: "#fff",
              p: 2,
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            }}
          >
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box
          sx={{ p: 3, borderRadius: 3, backgroundColor: "background.paper" }}
        >
          <Skeleton variant="rectangular" width="100%" height={300} />
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box
          sx={{ p: 3, borderRadius: 3, backgroundColor: "background.paper" }}
        >
          <Skeleton variant="rectangular" width="100%" height={300} />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{ p: 3, borderRadius: 3, backgroundColor: "background.paper" }}
        >
          <Skeleton variant="rectangular" width="100%" height={350} />
        </Box>
      </Grid>
    </Grid>
  </Box>
);
